import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { formatPhoneUS, displayPhone, digitsOnly } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, User, Folders, PencilSimple, Trash, Eye, UserSwitch, SquaresFour, List as ListIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useImpersonation } from "../context/ImpersonationContext";

const EMPTY = { name: "", email: "", phone: "", notes: "" };
const VIEW_KEY = "ll_clients_view"; // "grid" | "list"

export default function Clients() {
    const { user } = useAuth();
    const { start: startImpersonation } = useImpersonation();
    const nav = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = create mode, {id, ...} = edit
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null); // client doc pending deletion
    const [deleting, setDeleting] = useState(false);
    const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "grid");

    useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);

    const load = () => {
        api.get("/clients").then((r) => setClients(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (c) => {
        setEditing(c);
        setForm({ name: c.name || "", email: c.email || "", phone: c.phone || "", notes: c.notes || "" });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/clients/${editing.id}`, form);
                toast.success("Client updated");
            } else {
                await api.post("/clients", form);
                toast.success("Client added");
            }
            setForm(EMPTY);
            setEditing(null);
            setOpen(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const confirmDelete = async () => {
        if (!confirmDel) return;
        setDeleting(true);
        try {
            await api.delete(`/clients/${confirmDel.id}`);
            toast.success("Client deleted");
            setConfirmDel(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setDeleting(false); }
    };

    const impersonate = (c) => {
        startImpersonation(c);
        toast.success(`Now focused on ${c.name}`);
        nav("/dashboard");
    };

    if (user?.role !== "business") {
        return (
            <div className="max-w-xl">
                <p className="overline">Restricted</p>
                <h1 className="font-serif text-4xl mt-3">Clients is for business accounts</h1>
                <p className="mt-3 text-slate-600">Upgrade your account type to track multiple clients and case loads.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="clients-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="overline">Roster</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Clients</h1>
                </div>
                <div className="flex items-center gap-3">
                    <div
                        className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50"
                        role="group"
                        aria-label="View mode"
                        data-testid="clients-view-toggle"
                    >
                        <button
                            type="button"
                            onClick={() => setView("grid")}
                            data-testid="clients-view-grid"
                            aria-pressed={view === "grid"}
                            title="Grid view"
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded text-xs font-medium transition-colors ${
                                view === "grid"
                                    ? "bg-white text-[#0F1E3D] shadow-sm"
                                    : "text-slate-500 hover:text-slate-800"
                            }`}
                        >
                            <SquaresFour size={14} weight={view === "grid" ? "fill" : "regular"} />
                            <span className="hidden sm:inline">Grid</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setView("list")}
                            data-testid="clients-view-list"
                            aria-pressed={view === "list"}
                            title="List view"
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded text-xs font-medium transition-colors ${
                                view === "list"
                                    ? "bg-white text-[#0F1E3D] shadow-sm"
                                    : "text-slate-500 hover:text-slate-800"
                            }`}
                        >
                            <ListIcon size={14} weight={view === "list" ? "bold" : "regular"} />
                            <span className="hidden sm:inline">List</span>
                        </button>
                    </div>
                    <Button data-testid="client-new-btn" onClick={openCreate} className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-10">
                        <Plus size={16} className="mr-1" /> Add client
                    </Button>
                </div>
            </div>

            {loading ? (
                <p className="text-slate-500">Loading…</p>
            ) : clients.length === 0 ? (
                <div className="border border-dashed border-slate-300 rounded-md p-12 text-center">
                    <p className="text-slate-600">No clients yet.</p>
                    <Button onClick={openCreate} className="mt-4 bg-[#0F1E3D] hover:bg-[#1A2C57]">Add your first client</Button>
                </div>
            ) : view === "list" ? (
                <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="clients-list">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Name</th>
                                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Email</th>
                                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Phone</th>
                                <th className="text-left px-4 py-3 font-medium w-24">Cases</th>
                                <th className="text-right px-4 py-3 font-medium w-[300px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="clients-list-body">
                            {clients.map((c) => (
                                <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-slate-200 hover:bg-slate-50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-8 h-8 rounded-full bg-[#0F1E3D] text-white flex items-center justify-center shrink-0">
                                                <User size={14} weight="duotone" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-slate-900 truncate">{c.name}</p>
                                                {c.notes && <p className="text-xs text-slate-500 truncate max-w-xs">{c.notes}</p>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell truncate max-w-xs">{c.email || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{displayPhone(c.phone) || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                                            <Folders size={12} /> {c.case_count || 0}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => nav(`/clients/${c.id}`)}
                                                data-testid={`client-view-${c.id}`}
                                                className="h-8 px-2 text-xs font-medium border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-[#0F1E3D]"
                                                title="View client detail"
                                                aria-label={`View ${c.name}`}
                                            >
                                                <Eye size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">View</span>
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openEdit(c)}
                                                data-testid={`client-edit-${c.id}`}
                                                className="h-8 px-2 text-xs font-medium border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-[#0F1E3D]"
                                                title="Edit client"
                                                aria-label={`Edit ${c.name}`}
                                            >
                                                <PencilSimple size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">Edit</span>
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => impersonate(c)}
                                                data-testid={`client-impersonate-${c.id}`}
                                                className="h-8 px-2 text-xs font-medium border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                                title="Enter client focus mode"
                                                aria-label={`Focus on ${c.name}`}
                                            >
                                                <UserSwitch size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">Focus</span>
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setConfirmDel(c)}
                                                data-testid={`client-delete-${c.id}`}
                                                className="h-8 px-2 text-xs font-medium border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                                title="Delete client"
                                                aria-label={`Delete ${c.name}`}
                                            >
                                                <Trash size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">Delete</span>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
                    {clients.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`client-card-${c.id}`}
                            className="relative p-6 bg-white border border-slate-200 rounded-md hover:-translate-y-0.5 hover:shadow-sm transition-all"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#0F1E3D] text-white flex items-center justify-center shrink-0">
                                    <User size={18} weight="duotone" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold truncate">{c.name}</p>
                                    <p className="text-xs text-slate-500 truncate">{c.email || displayPhone(c.phone) || "—"}</p>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center gap-2 text-xs text-slate-600">
                                <Folders size={14} /> <span>{c.case_count || 0} cases</span>
                            </div>
                            {c.notes && <p className="mt-3 text-xs text-slate-500 line-clamp-2">{c.notes}</p>}

                            <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-4 gap-1.5">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => nav(`/clients/${c.id}`)}
                                    data-testid={`client-view-${c.id}`}
                                    className="h-8 px-1 text-xs font-medium border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-[#0F1E3D] min-w-0"
                                    title="View client detail"
                                    aria-label={`View ${c.name}`}
                                >
                                    <Eye size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">View</span>
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEdit(c)}
                                    data-testid={`client-edit-${c.id}`}
                                    className="h-8 px-1 text-xs font-medium border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-[#0F1E3D] min-w-0"
                                    title="Edit client"
                                    aria-label={`Edit ${c.name}`}
                                >
                                    <PencilSimple size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">Edit</span>
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => impersonate(c)}
                                    data-testid={`client-impersonate-${c.id}`}
                                    className="h-8 px-1 text-xs font-medium border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 min-w-0"
                                    title="Enter client focus mode"
                                    aria-label={`Focus on ${c.name}`}
                                >
                                    <UserSwitch size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">Focus</span>
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setConfirmDel(c)}
                                    data-testid={`client-delete-${c.id}`}
                                    className="h-8 px-1 text-xs font-medium border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 min-w-0"
                                    title="Delete client"
                                    aria-label={`Delete ${c.name}`}
                                >
                                    <Trash size={13} className="sm:mr-1 shrink-0" /> <span className="hidden sm:inline">Delete</span>
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">
                            {editing ? "Edit client" : "New client"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3" data-testid="client-form">
                        <div>
                            <Label htmlFor="cn">Name *</Label>
                            <Input data-testid="client-name" id="cn" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label htmlFor="ce">Email</Label>
                            <Input data-testid="client-email" id="ce" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" />
                        </div>
                        <div>
                            <Label htmlFor="cp">Phone</Label>
                            <Input
                                data-testid="client-phone"
                                id="cp"
                                inputMode="tel"
                                placeholder="(555) 123-4567"
                                value={formatPhoneUS(form.phone)}
                                onChange={(e) => setForm({ ...form, phone: digitsOnly(e.target.value) })}
                                className="mt-1.5 h-11"
                            />
                        </div>
                        <div>
                            <Label htmlFor="cnt">Notes</Label>
                            <Textarea data-testid="client-notes" id="cnt" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
                        </div>
                        <DialogFooter>
                            <Button type="submit" disabled={saving} data-testid="client-save-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">
                                {saving ? "Saving…" : editing ? "Save changes" : "Add client"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
                <AlertDialogContent data-testid="client-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl">Delete this client?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDel?.name ? <>You&apos;re about to permanently delete <strong>{confirmDel.name}</strong>.</> : null}
                            {confirmDel?.case_count > 0 && (
                                <span className="block mt-2 text-amber-700">
                                    {confirmDel.case_count} linked case{confirmDel.case_count === 1 ? "" : "s"} will be kept but unlinked from this client.
                                </span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="client-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="client-delete-confirm"
                        >
                            {deleting ? "Deleting…" : "Delete client"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
